import { useEffect, useId, useMemo, useRef, useState } from "react";
import { flattenLessons, type Course } from "@/core/types";
import { Icon } from "@/ui/components/Icon";
import { href } from "./useHashRoute";

interface LessonSearchProps {
  courses: Course[];
}

/** Kleinschreibung ohne Akzente: „uberschrift“ findet „Überschrift“. */
function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

const MAX_RESULTS = 8;

/**
 * Suche über alle Lektionen aller Kurse. Treffer im Lektionstitel stehen vor
 * Treffern im Modul oder in einem Schritt. Strg + K (Mac: ⌘ + K) springt ins
 * Feld, Pfeiltasten wählen, Enter öffnet, Escape schließt.
 */
export function LessonSearch({ courses }: LessonSearchProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const index = useMemo(
    () =>
      courses.flatMap((course) =>
        flattenLessons(course).map(({ module, lesson }) => ({
          course,
          module,
          lesson,
          title: normalize(lesson.title),
          context: normalize(
            [module.title, ...lesson.steps.map((step) => step.title)].join(" "),
          ),
        })),
      ),
    [courses],
  );

  const results = useMemo(() => {
    const words = normalize(query).split(/\s+/).filter(Boolean);
    if (words.length === 0) return [];
    return index
      .map((entry) => {
        const inTitle = words.every((word) => entry.title.includes(word));
        const anywhere = words.every(
          (word) => entry.title.includes(word) || entry.context.includes(word),
        );
        return { entry, rank: inTitle ? 0 : anywhere ? 1 : 2 };
      })
      .filter(({ rank }) => rank < 2)
      .sort((a, b) => a.rank - b.rank)
      .slice(0, MAX_RESULTS)
      .map(({ entry }) => entry);
  }, [index, query]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (target: (typeof results)[number] | undefined) => {
    if (!target) return;
    window.location.hash = href.lesson(target.course.id, target.module.id, target.lesson.id);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const showList = open && query.trim().length > 0;
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

  return (
    <div className="search">
      <Icon name="search" className="search__icon" />
      <input
        ref={inputRef}
        className="search__input"
        type="search"
        placeholder="Lektion suchen"
        aria-label="Lektion suchen"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && results[active] ? `${listId}-${active}` : undefined}
        autoComplete="off"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive((current) => Math.min(current + 1, results.length - 1));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive((current) => Math.max(current - 1, 0));
          } else if (event.key === "Enter") {
            go(results[active]);
          } else if (event.key === "Escape") {
            setQuery("");
            inputRef.current?.blur();
          }
        }}
      />
      <kbd className="search__hint" aria-hidden="true">
        {isMac ? "⌘" : "Strg"} K
      </kbd>

      {showList ? (
        <ul className="search__results" id={listId} role="listbox" aria-label="Treffer">
          {results.length === 0 ? (
            <li className="search__empty">Keine Lektion zu „{query.trim()}“.</li>
          ) : (
            results.map((entry, position) => (
              <li
                key={`${entry.course.id}/${entry.module.id}/${entry.lesson.id}`}
                id={`${listId}-${position}`}
                role="option"
                aria-selected={position === active}
                className="search__result"
                data-host={entry.course.host}
                // mousedown statt click: sonst schließt blur die Liste vorher.
                onMouseDown={(event) => {
                  event.preventDefault();
                  go(entry);
                }}
                onMouseEnter={() => setActive(position)}
              >
                <span className="search__title">{entry.lesson.title}</span>
                <span className="search__meta">
                  <span className="search__dot" aria-hidden="true" />
                  {entry.course.host} · {entry.module.title}
                </span>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
